import React, { useState, useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import classnames from 'classnames';

import { UnitFromQuickViewType } from 'types';
import phone from '../../public/images/phone.svg';
const ContactUsDialog = dynamic(() => import('./ContactUsDialog'), {
  ssr: false,
});
import { Agent } from 'apolloClient/types/Agents';
import { usePhoneNumber } from '../../hooks/usePhoneNumber';
import { Listing } from 'apolloClient/types/Units';
import { ContactFormLocation } from 'src/gtm';

export type PopUpContactUsProps = {
  flagPosition?: string;
  phoneIcon?: string;
  phoneNumber?: string;
  showName?: string;
  flagContainer?: string;
  contactUsName?: string;
  isRequestInfoBuilding?: boolean;
  searchQuickView?: boolean;
  isRequestInfoUnitOwner?: boolean;
  isRequestInfoBuildingOwner?: boolean;
  contactUsNotButton?: string;
  contactUsNotButtonClassName?: string;
  listingFromQuickView?: Listing;
  unitFromQuickView?: UnitFromQuickViewType;
  experts?: Agent[] | undefined;
  isMobile?: boolean;
  gtmId?: string;
  isNavigation?: boolean;
  customText?: string;
  formLocation?: ContactFormLocation;
};

const PopUpContactUs: React.FC<PopUpContactUsProps> = ({
  gtmId = 'gtm-contact-us',
  flagPosition = 'left-[-0.6rem] s:-left-4',
  phoneIcon = 'md:hidden',
  phoneNumber = 'hidden',
  showName = 'hidden',
  flagContainer = 'pb-0.5 pt-[3px] md:pb-[8px] md:pt-[8px] pr-2 h-[31px] s:h-[33px]',
  contactUsName,
  isRequestInfoBuilding,
  isRequestInfoUnitOwner,
  isRequestInfoBuildingOwner,
  searchQuickView,
  contactUsNotButton,
  contactUsNotButtonClassName = 'text-gold-dark',
  listingFromQuickView,
  unitFromQuickView,
  experts,
  isMobile,
  isNavigation,
  customText,
  formLocation = 'page_popup',
}) => {
  const router = useRouter();

  const [open, setOpen] = useState(false);

  const { areaCode, phoneEnding } = usePhoneNumber({
    open,
    routerPath: router.asPath,
  });

  const handlePopUpClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const openModalIfHashIsContact = () => {
      if (window.location.hash === '#contact') {
        setOpen(true);

        // Delete the hash from the URL
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState(null, '', url.toString());
      }
    };

    openModalIfHashIsContact(); // initial
    window.addEventListener('hashchange', openModalIfHashIsContact);

    return () =>
      window.removeEventListener('hashchange', openModalIfHashIsContact);
  }, []);

  useEffect(() => {
    if (window.location.hash === '#contact' && !open && router.isReady) {
      setOpen(true);

      // Delete the hash from the URL
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.replaceState(null, '', url.toString());
    }
  }, [router.isReady]);

  return (
    <>
      {contactUsNotButton && (
        <button
          onClick={() => setOpen(true)}
          className={contactUsNotButtonClassName}
        >
          {contactUsNotButton}
        </button>
      )}
      {contactUsName ? (
        <button
          id="contact-us-wrapper"
          onClick={() => setOpen(true)}
          className={classnames({
            'hover-animation px-[8px] xl:px-6 py-[11px] uppercase min-w-[140px] xl:w-72 bg-gold hover:bg-gold-hover text-16-16-0.3':
              isRequestInfoBuilding,
            'hover-animation px-[8px] xl:px-6 py-2 uppercase w-[140px] xl:w-full xl:max-w-[250px] xl:py-[11px] bg-gold hover:bg-gold-hover text-16-21-0.3':
              !isRequestInfoBuilding,
            'hover-animation !px-[8px] py-2 uppercase w-[140px] xl:!w-[140px] xl:max-w-[152px] xl:py-[11px] bg-gold hover:bg-gold-hover text-16-21-0.3 font-SohneBuch h-[44px]':
              searchQuickView,
          })}
        >
          <div id="contact-us-wrapper" className="text-16-16-0.3">
            {contactUsName}
          </div>
        </button>
      ) : (
        !contactUsNotButton && (
          <button
            id={gtmId}
            onClick={() => setOpen(true)}
            className="relative items-center flex-none outline-none cursor-pointer md:flex"
          >
            <div className="relative items-center cursor-pointer xl:ml-6 overflow-hidden">
              <div
                className={`absolute z-30 h-5 w-5 s:w-8 s:h-8 transform rotate-45 -translate-y-1/2 bg-white top-[49%] md:top-1/2 ${flagPosition}`}
              />
              <div
                className={classnames(
                  `
                  hover-animation uppercase bg-gold hover:bg-gold-hover font-sohneBreitHalbfett
                  text-14-17-0.3 s:text-16-16-0.3 md:text-14-18-0.3 lg:text-16-16-0.3
                  ${
                    isNavigation
                      ? `pl-[16px]
                         pr-[14px] sm:pr-[34px]
                         md:h-[35px] md:pt-[8px]`
                      : `pl-[16px] sm:pl-[22px] md:pl-[36px] lg:pl-[37px]
                         pr-[14px] sm:pr-[14px] md:pr-[45px] lg:pr-[13px]
                         md:h-[35px] md:pt-[8px] s:pl-[16px]`
                  }
                  ${flagContainer}
                `,
                  {
                    'pr-[12px] pl-[8px] s:pl-[22px] pb-[3px] s:!pb-[30px]':
                      isMobile,
                  }
                )}
              >
                <span
                  className={`md:inline-block font-sohneBuch text-black pr-4 ${showName}`}
                >
                  Contact us
                </span>
                <span
                  className={`pt-px md:inline-block text-black  ${phoneNumber}`}
                >
                  <span>(</span>
                  <span className="text-black font-sohneKraftig ">
                    {areaCode}
                  </span>
                  <span>)</span>{' '}
                  <span className="text-black font-sohneKraftig ">
                    {phoneEnding}
                  </span>
                </span>
                <span className={`pl-[20px] s:pl-[26px] h-[26px] ${phoneIcon}`}>
                  <Image src={phone} alt="phone" width={24} height={24} />
                </span>
              </div>
            </div>
          </button>
        )
      )}
      <ContactUsDialog
        open={open}
        onClose={handlePopUpClose}
        isRequestInfoBuilding={isRequestInfoBuilding}
        isRequestInfoBuildingOwner={isRequestInfoBuildingOwner}
        isRequestInfoUnitOwner={isRequestInfoUnitOwner}
        listingFromQuickView={listingFromQuickView}
        unitFromQuickView={unitFromQuickView}
        experts={experts}
        phoneEnding={phoneEnding}
        areaCode={areaCode}
        customText={customText}
        formLocation={formLocation}
      />
    </>
  );
};

export default PopUpContactUs;
